import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  CardContent,
  Grid,
  Theme,
  Typography,
  Tab,
  Button,
  Tooltip,
  BoxProps,
  Box,
  styled,
  alpha,
} from "@mui/material";

// Components
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IBidPackage,
  IBidPackageResponseListItem,
  IBidPackageRequest,
  IBidPackageResponse,
  IBidPackageNonFlyingAssignments,
  IPosition,
} from "@vmosolutions/oc-interfaces";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { Form, Formik, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikSelectField,
  FormikCheckboxField,
  FormikDateTimeField,
} from "src/components/FormikComponents/FormikWrappers";
// Custom Fields
import { NFATypeCountField } from "./NFATypeCountField";
import { CrewGroupsField } from "./CrewInPackage/CrewGroupsField";
import * as Yup from "yup";
import { updateTabNodeByWindowIdAndProps } from "src/redux/actions/Mosaic.Actions";
import * as _ from "lodash";
import { BidPackageSummary } from "./BidPackageSummary/BidPackageSummary";
import { getBidPackage, ICrewBid } from "./helpers";
import { PairingSearch } from "./PairingSearch/PairingSearch";
import { BidPairingView } from "./BidPairingView/BidPairingView";
import { getFormFieldDiff } from "src/utils/index";
import { ModalTitle, ModalMessage, ModalButton } from "src/components/DialogModal/DialogModalComponents";
import DialogContent from "@mui/material/DialogContent";
import { DialogActions } from "@mui/material";

interface IBidPackageEditorProps {
  id: string;
  path: string[];
  windowProps: {
    bidPackagelistItem?: IBidPackageResponseListItem;
    bidPackage?: IBidPackage;
    duplicate?: boolean; // If duplicating an existing bid package, load bidpackge but clear id when initiating form.
  };
}

export interface IActiveModal {
  title: string;
  message: string;
  cancel: () => void;
  confirm: () => void;
  confirmLabel: string;
  list?: string[];
}

export interface ICustomTab {
  customSchema: string;
  tabIndex: number;
  title: string;
}

const initialValues: IBidPackage = {
  _id: undefined,
  isDisabled: false,
  bidWindowOpen: undefined,
  bidWindowClose: undefined,
  bidBlockStart: undefined,
  bidBlockEnd: undefined,
  releaseScheduleAt: undefined,
  title: undefined,
  nonFlyingAssignments: [], // IBidPackageNonFlyingAssignments[]
  positions: [],
  crewGroups: [],
  createdAt: undefined,
  createdBy: undefined,
  modifiedAt: undefined,
  modifiedBy: undefined,
};

interface IBidPackageNonFlyingAssignmentsPayload {
  nfaType: string; // for payload, nfaType is passed as an object id.
  maxAmount: number;
  _id?: string; // what appears to be an object id associated with nfatype counts.
}

//TODO Investigate why we're having to transform this data format in payload.
const convertNFACounts = (nfaCounts: IBidPackageNonFlyingAssignments[]): IBidPackageNonFlyingAssignmentsPayload[] => {
  return nfaCounts?.length
    ? nfaCounts?.map((n) => ({
        nfaType: n?.nfaType?._id,
        maxAmount: n?.maxAmount,
        _id: (n as any)?._id,
      }))
    : [];
};

const BidPackageEditorStyles = (theme: Theme): any => ({
  root: {
    display: "flex",
    width: "100%",
    minWidth: "944px",
    height: "100%",
    "& .loadingSpinner": {
      marginTop: "-50px",
      marginLeft: "-26px",
    },
    "& .content_pane": {
      width: "100%",
      height: "100%",

      "& .MuiAccordion-root.bid_package_editor": {
        margin: 0,
        backgroundColor: theme.custom.timeline.contextListBackground,
        transition: theme.transitions.create(["height"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.short,
        }),
        "& .MuiIconButton-edgeEnd": { marginRight: "-16px" },
        "& .MuiAccordionSummary-root": {
          minHeight: "48px",
          "& .MuiAccordionSummary-content.tab_header": {
            fontWeight: 700,
            margin: 0,
            minHeight: "48px",
            alignItems: "center",
            "& .MuiTabs-root": { height: "24px", margin: "0px 10px" },
          },
        },
        "& .MuiCollapse-root": {
          height: "calc(100% - 50px) !important",
          "& .MuiCollapse-wrapper": {
            margin: "15px",
            backgroundColor: theme.palette.background.paper,
            height: "calc(100% - 30px)",

            "& .MuiCollapse-wrapperInner": {
              height: "100%",
              "& div[role=region]": {
                height: "100%",
                "& .MuiAccordionDetails-root": {
                  height: "100%",
                  width: "100%",
                  alignItems: "flex-start",
                  ...theme.typography.body2,
                  "&.tab_1, &.tab_2, &.tab_3": {
                    overflowY: "auto",
                  },
                  "&.tab_2, &.tab_3": {
                    padding: 0,
                    backgroundColor: theme.custom.timeline.contextListBackground,
                  },
                  "& .tab_content": {
                    "& p.required_fields_label": {
                      margin: 0,
                      fontWeight: 300,
                      fontSize: 10,
                      textTransform: "uppercase",
                      color: theme.custom.forms.labels,
                    },
                    "& .required_fields": {
                      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
                      margin: "2px 0px",
                      padding: "10px",
                    },
                    "& .optional_fields": {
                      margin: "2px 0px",
                      padding: "10px",
                    },
                    // EMBEDDED FORM CONTROLS
                    "& .embedded_form_controls": {
                      display: "block",
                      border: `1px solid ${theme.palette.background.default}`,
                      padding: "10px",
                      width: "auto",
                    },

                    "& .crew_in_selected_groups": {
                      maxHeight: "calc(100% - 90px)",
                      minHeight: "24px",
                      overflowY: "auto",
                      backgroundColor: theme.palette.background.default,
                      paddingTop: "5px",
                      marginTop: "15px",
                      alignContent: "flex-start",
                      position: "relative",
                      "& .crew_option_container": {
                        width: "300px",
                        height: "20px",
                        margin: "0px 5px 5px 5px",
                        alignItems: "flex-end",
                      },
                    },
                    "& .crewLoadingSpinner": {
                      marginLeft: "-10px",
                      marginTop: "-10px",
                      width: "calc(100% - 50px)",
                      height: "calc(100% - 120px)",
                    },
                    "& .bid_window_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "purple",
                    },
                    "& .bid_block_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "teal",
                    },
                    "& .release_color_swatch": {
                      margin: "5px",
                      height: "24px",
                      width: "5px",
                      backgroundColor: "yellow",
                    },
                    "& .field": {
                      width: "auto",
                      minWidth: "238px",
                      "&.textfield": {
                        "&.title": {
                          "& .input_control": {
                            "& label": { width: "auto" },
                            "& .input-base": { width: "250px" },
                          },
                        },
                        "&.nfa_type_count": {
                          width: "auto",
                          minWidth: "auto",
                          margin: "0px 10px",
                          "& .input_control": {
                            "& label": { width: "auto" },
                            "& .input-base": { width: "50px" },
                          },
                        },
                        "&.multiline_short": {
                          width: "350px",
                          "& .input_control": {
                            "& label": { width: "auto" },
                          },
                        },
                        "&.multiline_long": {
                          width: "100%",
                          maxWidth: "540px",
                          "& .input_control": {
                            "& label": { width: "150px" },
                          },
                        },
                      },
                      "&.selectfield": {
                        "&.bid_pairing_status_filter": {
                          "& label": { width: "auto" },
                        },
                        "&.positions": {
                          width: "400px",
                          minWidth: "400px",
                          "& label": { width: "auto" },
                        },
                        "&.nfa_type_selector": {
                          width: "95%",
                          minWidth: "95%",
                          "& label": { width: "auto" },
                        },
                        "&.crewgroups": {
                          width: "calc(100% - 200px)",
                          minWidth: "calc(100% - 200px)",
                          "& label": { width: "auto" },
                        },
                      },
                      "&.datetimefield": {
                        "& label": { width: "100px" },
                      },
                      "&.checkboxfield": {
                        minWidth: "120px",
                      },
                    },

                    "& .grid_item_divider": {
                      width: "100%",
                      height: "1px",
                      border: "none",
                      backgroundColor: theme.custom.forms.labels,
                      marginBlockStart: "1em",
                      marginBlockEnd: "1em",
                      marginInlineStart: "1em",
                      marginInlineEnd: "1em",
                    },
                  },
                },
              },
            },
          },
        },
        "& .crew_change_buttons": {
          width: "auto",
          margin: "5px",
          "& .label": { color: theme.custom.forms.labels, marginRight: 10 },
          "& .icon": {
            width: "20px",
            height: "20px",
            cursor: "pointer",
            "&.removed.active": { color: theme.palette.error.main },
            "&.nochange.active": { color: theme.palette.common.white },
            "&.added.active": { color: theme.palette.success.main },
          },
        },
        "& .force_crew_rebuild": {
          width: "auto",
          "& .label": { marginLeft: "10px" },
        },
        "& .crew_on_bidpackage_table": {
          marginTop: "10px",
        },
        "& .add_pairing_container": {
          margin: 0,
          backgroundColor: theme.custom.timeline.contextListBackground,
        },
        "& .pairing_search_table": {
          overflowX: "hidden",
          overflowY: "hidden",
          "& .MuiAccordion-root": {
            margin: "5px 0px",
            "&.Mui-expanded .MuiCollapse-root": {
              height: "auto !important",
            },
            "& .MuiCollapse-root.MuiCollapse-hidden": { height: "0px !important" },
          },
        },
      },

      "& .form_actions": {
        width: "auto",
        marginLeft: "auto",
        marginRight: 0,
        "& .MuiButtonBase-root": {
          padding: "12px 7px",
          "&.reset,&.save,&.refresh,&.review": { padding: "2px 8px", margin: "0px 5px", fontSize: 12 },
        },
      },

      "& .refresh_btn": { padding: "2px 8px", margin: "5px 5px", fontSize: 12, height: "24px" },
    },
  },

  ModalDialogStyle: {
    marginTop: "-36px",
    marginLeft: "-10px",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    alignItems: "center",
    position: "absolute",
    zIndex: 9999,
    width: "100%",
    height: "100%",
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    "& .modal_content": {
      backgroundColor: theme.palette.background.paper,
      display: "flex",
      flexDirection: "column",
      alignContent: "center",
      justifyContent: "center",
      borderRadius: "3px",
      padding: "10px",
      margin: "48px",
      maxHeight: "calc(100% - 96px)",
      maxWidth: 600,
      position: "relative",
      boxShadow:
        "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
      "& .slider-container": {
        padding: "0px 10px 0px 10px",
      },
      "& .modal_status": {
        fontSize: 12,
        fontWeight: 300,
        color: theme.palette.primary.contrastText,
        paddingLeft: "25px",
      },
    },
  },
});

const TabStyles: React.FC<BoxProps> = styled(Box)(({ theme }) => ({
  "& .MuiCardContent-root": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    paddingBottom: "16px !important",

    "& .form_container": {
      height: "100%",
      "& form": {
        width: "100%",
      },
      "& .form_title": {
        padding: "4px 0px",
      },
    },
    "& .form_title": {
      margin: "0px 10px",
      color: theme.custom.forms.labels,
      "& .deselect_btn": {
        margin: "0px 5px",
        color: theme.custom.forms.labels,
        padding: 0,
        borderRadius: 3,
        verticalAlign: "sub",
      },
    },
    "& .left_pane": {
      height: "100%",
      width: 230,
      "& .react-select__menu": {
        maxHeight: "auto",
      },
    },
    "& .right_pane": {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      marginLeft: 15,
      width: "calc(100% - 230px)",
      "& .content": {
        color: "red",
        height: "calc(100% - 40px)",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      },
    },
  },
}));

// IContent Form
export type IBidPackageForm = FormikProps<IBidPackage>;

// Validation Schema
const BidPackageSchema = Yup.object().shape({
  // Details tab fields
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must contain at least 3 characters")
    .max(30, "Title cannot exceed 30 characters"),
  positions: Yup.array().required().min(1, "At least one assignment position is required"),
  bidWindowOpen: Yup.date().nullable().required("Bid Window Open is Required"),
  bidWindowClose: Yup.date()
    .nullable()
    .required("Bid Window Close is Required")
    .test("bidWindowOpen", "Can't match or preceed window open", (date, y) => {
      return !date || !y.parent?.bidWindowOpen || y.parent?.bidWindowOpen?.valueOf() < date?.valueOf();
    })
    .test("bidWindowOpen", "Bid Window cannot exceed 400 days", (date, y) => {
      const difference: number = (date?.valueOf() - y.parent?.bidWindowOpen?.valueOf()) / 1000 / 60 / 60 / 24;
      return difference && difference < 400;
    }),
  //.when("bidWindowOpen", (date, y) => date && y.min(date, "Close date cannot be before window open")),
  bidBlockStart: Yup.date()
    .nullable()
    .required("Bid Block Start is Required")
    .test("bidWindowClose", "Can't match or preceed bid window close", (date, y) => {
      return !date || !y.parent?.bidWindowClose || y.parent?.bidWindowClose?.valueOf() < date?.valueOf();
    }),
  //.when("bidWindowClose", (date, y) => date && y.min(date, "Bid Block cannot start before bid window closes")),
  bidBlockEnd: Yup.date()
    .nullable()
    .required("Bid Block End is Required")
    .test("bidBlockStart", "Can't match or preceed bid block start", (date, y) => {
      return !date || !y.parent?.bidBlockStart || y.parent?.bidBlockStart?.valueOf() < date?.valueOf();
    })
    .test("bidBlockEnd", "Bid Block cannot exceed 400 days", (date, y) => {
      const difference: number = (date?.valueOf() - y.parent?.bidBlockStart?.valueOf()) / 1000 / 60 / 60 / 24;
      return difference && difference < 400;
    }),
  //.when("bidBlockStart", (date, y) => date && y.min(date, "End date cannot be before block start")),
  releaseScheduleAt: Yup.date().nullable(),
  nonFlyingAssignments: Yup.array().of(
    Yup.object().shape({
      nfaType: Yup.object(),
      maxAmount: Yup.number().required().min(1, "At least 1 required for count").max(99, "Cannot exceed 99 for count"),
    })
  ),
});

// Small component used to pass Tab props into it's component when using wrapper on them
// wrapper required in order to display Tooltips on disabled tabs.
// https://www.appsloveworld.com/reactjs/200/559/tooltip-on-disabled-tab-material-ui-react
function CloneProps(props: any): JSX.Element {
  const { children, ...other } = props;
  return children(other);
}

type UpdatePackage = (
  values: IBidPackage,
  helpers: FormikHelpers<IBidPackage>,
  initialValues: IBidPackage
) => Promise<void>;

/**
 * Returns a modal object to load into the dialog modal that the user will interact with.
 * @param type - modal type
 * @param updatePackage - confirm action
 * @param cancel - cancel action
 * @returns - modal object.
 */
const returnModalWarning = (
  type: string,
  updatePackage: UpdatePackage,
  setActiveModal: React.Dispatch<React.SetStateAction<IActiveModal>>,
  v: IBidPackage,
  h: FormikHelpers<IBidPackage>,
  initialFormValues: IBidPackage
): IActiveModal | null => {
  switch (type) {
    case "disablePackage":
      return {
        title: "Disable Bid Package?",
        message:
          "Are you certain you wish to disable this bid package? Doing so will remove package availability from associated crew members.",
        cancel: () => {
          h?.setSubmitting(false);
          setActiveModal(null);
        },
        confirm: () => {
          updatePackage(v, h, initialFormValues);
          setActiveModal(null);
        },
        confirmLabel: "Disabled",
      };
    default:
      return null;
  }
};

/**
 * Bid Package Editor - allows creation and updates of bid packages used for assignment of pairings to crew.
 */
export const BidPackageEditorWindow = (props: IBidPackageEditorProps): JSX.Element => {
  const { id, path, windowProps } = props;

  const dispatch = useDispatch();

  const [activeModal, setActiveModal] = useState<IActiveModal | null>(null);
  const [initialFormValues, setInitialFormValues] = useState<IBidPackage>(initialValues);

  // Store information pertaining to crew bids and bid pairings.
  const [crewBids, setCrewBids] = useState<ICrewBid[]>([]);
  const [bidPairingCount, setBidPairingCount] = useState<{ pairingId: string; bidPairingId: string }[]>([]);

  // Should only be set true if the crew groups property is dirty.
  const [populateCrew, setPopulateCrew] = useState<boolean>(false);

  // If id associated with windowprops does not match id stored in form, re-initialize form.
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getPackage = useCallback(async (packageId: string, duplicate?: boolean): Promise<void> => {
    setIsLoading(true);
    const response: IBidPackageResponse = await getBidPackage(packageId);
    if (response?.bidPackage?._id) {
      const newValues: IBidPackage = duplicate ? { ...response?.bidPackage, _id: undefined } : response?.bidPackage;
      // releaseScheduleAt disabled for the time being, make sure it's never initialized in the form.
      setInitialFormValues({ ...newValues, releaseScheduleAt: undefined });
      const newCrewBids: any[] = Array.isArray(response?.crewBids) ? response?.crewBids : [];
      setCrewBids(newCrewBids as ICrewBid[]); // override backend interface here as there are conflicts with ObjectId types.
      const newBidPairings = Array.isArray(response?.bidPairings) ? response?.bidPairings : [];
      setBidPairingCount(newBidPairings);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!!windowProps?.bidPackagelistItem?._id && windowProps?.bidPackagelistItem?._id !== initialFormValues?._id) {
      // If window props contain a bid package list item id, attempt to load it.
      getPackage(windowProps?.bidPackagelistItem?._id, windowProps?.duplicate);
    }
  }, [windowProps, getPackage, initialFormValues?._id]);

  // If bidPackage has been assigned to windowProps and doesn't match initial values, re-initialize form.
  useEffect(() => {
    if (!!windowProps?.bidPackage?._id && !_.isEqual(windowProps?.bidPackage, initialFormValues)) {
      setInitialFormValues(windowProps?.bidPackage);
    }
  }, [windowProps?.bidPackage, initialFormValues]);

  // If no _id is present, consider bid package new.
  const isNew = useMemo(() => !initialFormValues?._id, [initialFormValues]);

  /**
   * Function used to update the mosaic window when user has created a new package and
   * window is switched into update mode whilst still open.
   */
  const updateMosaicWindow = useCallback(
    (bidPackage: IBidPackage) => {
      dispatch(
        updateTabNodeByWindowIdAndProps(
          id, // mosaic id
          {
            title: "Edit Bid Package", // new window title
            props: { bidPackage }, // new window props
          },
          // Find this mosaic node in the Tree using the info below.
          windowProps,
          "BidPackageEditorWindow"
        )
      );
    },
    [dispatch, id, windowProps]
  );

  const [summaryHeight, setSummaryHeight] = useState<string>("160px");
  const [formHeight, setFormHeight] = useState<string>("calc(100% - 160px)");

  // Available Position Options
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );

  const getFullPositions = useCallback(
    (posTitles: string[]): IPosition[] => {
      const selectedFullPositions: IPosition[] = [];
      posTitles.forEach((title: string) => {
        const fullPos = positions.find((pos) => pos.title === title);
        if (fullPos) selectedFullPositions.push(fullPos);
      });
      return selectedFullPositions;
    },
    [positions]
  );

  // Package status for bid pairing creation (stored here as it's used in both pairing search and bid pairing tables).
  const bidPairingCreationProgressMap = useSelector((state: IRootState) => state.bidding.bidPairingCreationProgressMap);
  const bidPairingProgressStatus = useMemo(
    () => (!!initialFormValues?._id && bidPairingCreationProgressMap?.[initialFormValues?._id]) ?? null,
    [bidPairingCreationProgressMap, initialFormValues?._id]
  );

  /**Return progress percentage to be displayed in a determinate progress bar above tables. */
  const bidPairingprogressPercentage = useMemo(() => {
    const errorCount = bidPairingProgressStatus?.error ?? 0;
    const notApplicableCount = bidPairingProgressStatus?.notApplicable ?? 0;
    const pendingCount = bidPairingProgressStatus?.pending ?? 0;
    const populatedCount = bidPairingProgressStatus?.populated ?? 0;
    const total = errorCount + notApplicableCount + pendingCount + populatedCount;
    const completed = errorCount + notApplicableCount + populatedCount;
    return total === 0 ? 0 : (completed * 100) / total;
  }, [bidPairingProgressStatus]);

  const handleToggleSummary = (event: React.ChangeEvent<object>): void => {
    const infoIsOpen = formHeight !== "50px";
    const summaryIsOpen = summaryHeight !== "50px";
    const newImageHeight =
      infoIsOpen && !summaryIsOpen ? "160px" : !infoIsOpen && !summaryIsOpen ? "calc(100% - 50px)" : "50px";
    setSummaryHeight(newImageHeight);
    if (infoIsOpen) {
      const infoHeight =
        newImageHeight === "50px"
          ? "calc(100% - 50px)"
          : newImageHeight === "calc(100% - 50px)"
            ? "50px"
            : "calc(100% - 160px)";
      setFormHeight(infoHeight);
    }
  };

  const handleToggleForm = (event: React.ChangeEvent<object>, isExpanded: boolean): void => {
    const summaryIsOpen = summaryHeight !== "50px";
    const newInfoHeight =
      isExpanded && summaryIsOpen ? "calc(100% - 160px)" : isExpanded && !summaryIsOpen ? "calc(100% - 50px)" : "50px";
    setFormHeight(newInfoHeight);
    if (summaryIsOpen) {
      const imageHeight =
        newInfoHeight === "50px" ? "calc(100% - 50px)" : newInfoHeight === "calc(100% - 50px)" ? "50px" : "160px";
      setSummaryHeight(imageHeight);
    }
  };
  const [tabValue, setTabValue] = useState<number>(0);

  const handleSelectTab = (event: any, newValue: number): void => {
    event.preventDefault();
    event.stopPropagation();
    setTabValue(newValue);
  };

  // TODO investigate why this isn't always returning dom element when used with useMemo
  const mosaicPane = id ? document.getElementById(`${id}-panel`) : null;

  const handleCreateUpdatePackage = useCallback(
    async (values: IBidPackage, helpers: FormikHelpers<IBidPackage>, initialValues: IBidPackage): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      setSubmitting(true);

      // Return object containing only dirty fields if editing. (spread these into new objects so that converting nfa counts doesn't break form nfa counts.)
      const payload: Partial<IBidPackageRequest> = isNew
        ? { ...values }
        : getFormFieldDiff({ ...values }, initialValues);

      if (payload?.nonFlyingAssignments?.length) {
        //@ts-ignore (transform NFAType counts into format endpoint expects)
        payload.nonFlyingAssignments = convertNFACounts(payload.nonFlyingAssignments);
      }

      // If populateCrew is set true pass the crewGroups value even if not dirty as backend will rebuild crew list.
      if (populateCrew && !payload?.crewGroups) {
        payload.crewGroups = values?.crewGroups;
      }

      //console.log("payload", values);
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${isNew ? "Building New" : "Updating"} Bid Package...`,
        successMessage: `Bid Package ${isNew ? "Built" : "Updated"} Successfully`,
        errorMessage: `Error: Unable to ${isNew ? "Built" : "Update"} New Bid Package`,
      };

      const response: any = isNew
        ? await AmplifyAPIWrapper.Post<IBidPackage>("/bidding/package", payload, notifications)
        : await AmplifyAPIWrapper.Put<IBidPackage>(
            `/bidding/package/${values?._id}?populateCrew=${populateCrew}`,
            payload,
            notifications
          );

      if (response?.bidPackage?._id) {
        if (Array.isArray(response?.crewBids)) {
          // if response contains crewBids, update the list of associated crew in this package.
          setCrewBids(response.crewBids as ICrewBid[]);
        }
        // Set the mosaic window in update mode. (will also re-initialize form);
        updateMosaicWindow(response?.bidPackage);
      }
      setPopulateCrew(false); // Reset populate crew back to false.
      setSubmitting(false);
    },
    [dispatch, isNew, updateMosaicWindow, populateCrew]
  );

  return (
    <>
      <TabStyles>
        <CardContent sx={(theme) => BidPackageEditorStyles(theme).root}>
          <SpinnerProgress className="loadingSpinner" loading={isLoading} message="loading package" />
          <Formik
            className={"flightlog_form"}
            initialValues={initialFormValues}
            validationSchema={BidPackageSchema}
            enableReinitialize={true}
            onSubmit={(v: IBidPackage, h: FormikHelpers<IBidPackage>) => {
              if (!!v?._id && v?.isDisabled) {
                // If user has set isDisabled true on an existing package, pop up warning modal.
                setActiveModal(
                  returnModalWarning(
                    "disablePackage",
                    handleCreateUpdatePackage,
                    setActiveModal,
                    v,
                    h,
                    initialFormValues
                  )
                );
              } else {
                handleCreateUpdatePackage(v, h, initialFormValues);
              }
            }}
          >
            {(bidPackageForm: IBidPackageForm) => {
              const { values, dirty, isValid, isSubmitting, handleReset, setFieldValue } = bidPackageForm;

              return (
                <Form style={{ height: "100%", width: "100%" }}>
                  <Grid container direction="row" wrap="nowrap" className="form_container">
                    <Grid className="content_pane">
                      <Grid
                        container
                        className={`${summaryHeight !== "50px" ? "open" : "closed"}`}
                        direction="column"
                        style={{ height: summaryHeight }}
                      >
                        <Grid container style={{ padding: "10px 0px", height: "100%" }}>
                          <BidPackageSummary
                            id={id}
                            path={path}
                            bidPackage={values}
                            bidPairingCount={bidPairingCount?.length ?? 0}
                            crewCount={crewBids?.length}
                          />
                        </Grid>
                      </Grid>

                      <Accordion
                        className="bid_package_editor"
                        defaultExpanded={true}
                        expanded={formHeight !== "50px"}
                        style={{ height: formHeight }}
                      >
                        <AccordionSummary classes={{ content: "tab_header" }} expandIcon={<ExpandMoreIcon />}>
                          <Grid container style={{ minWidth: 880 }} alignItems="center">
                            <Typography variant="subtitle2" style={{ fontSize: 16, marginRight: 10 }}>
                              Package Creation Steps
                            </Typography>
                            <OcTabs
                              variant="scrollable"
                              orientation="horizontal"
                              scrollButtons="auto"
                              value={tabValue}
                              onChange={handleSelectTab}
                            >
                              {" "}
                              <CloneProps>
                                {(tabProps: any) => (
                                  <Tooltip title="Bid Package details" placement="top">
                                    <div>
                                      <Tab
                                        wrapped={true}
                                        {...tabProps}
                                        label="1) Details"
                                        classes={{ root: "form-tab" }}
                                        // style={{  }}
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                              </CloneProps>
                              <CloneProps>
                                {(tabProps: any) => (
                                  <Tooltip
                                    title={
                                      isNew
                                        ? "Build package to unlock addition of crew"
                                        : "Add Crew by assigning desired groups"
                                    }
                                    placement="top"
                                  >
                                    <div>
                                      <Tab
                                        wrapped={true}
                                        {...tabProps}
                                        label="2) Add Crew"
                                        classes={{ root: "form-tab" }}
                                        //style={{ color: dirty ? theme?.custom?.forms?.dirtyColor : "inherit" }}
                                        disabled={isNew}
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                              </CloneProps>
                              <CloneProps>
                                {(tabProps: any) => (
                                  <Tooltip
                                    title={
                                      isNew
                                        ? "Build package unlock addition of pairings"
                                        : "Add/update pairing snapshots to the bidpackage"
                                    }
                                    placement="top"
                                  >
                                    <div>
                                      <Tab
                                        wrapped={true}
                                        {...tabProps}
                                        label="3) Add Pairings"
                                        classes={{ root: "form-tab" }}
                                        disabled={isNew}
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                              </CloneProps>
                              <CloneProps>
                                {(tabProps: any) => (
                                  <Tooltip
                                    title={
                                      isNew
                                        ? "Add bid pairings to unlock view"
                                        : "View/remove bid pairing snapshots from package"
                                    }
                                    placement="top"
                                  >
                                    <div>
                                      <Tab
                                        wrapped={true}
                                        {...tabProps}
                                        label={`4) View Bid Pairings (${bidPairingCount?.length})`}
                                        classes={{ root: "form-tab" }}
                                        disabled={isNew}
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                              </CloneProps>
                            </OcTabs>
                            <Grid container className="form_actions">
                              <Button
                                className="btn reset"
                                type="reset"
                                variant="contained"
                                color="secondary"
                                disabled={(!dirty && isValid) || isSubmitting}
                                onClick={(event) => {
                                  handleReset(event);
                                }}
                              >
                                Reset
                              </Button>
                              <Button
                                className={"btn save"}
                                // style={{ display: tabValue === "addPairings" ? "flex" : "none" }}
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting || !isValid || (!dirty && !populateCrew)}
                                type="submit"
                                //onClick={debounce(() => handleCreateUpdatePackage(values, bidPackageForm), 500)}
                              >
                                {tabValue === 0 && isNew ? "Build Package" : "Update Package"}
                              </Button>
                            </Grid>
                            <div
                              style={{ position: "absolute", zIndex: 9999, width: 48, height: 48, right: 0, top: 0 }}
                              onClick={(e: any) => {
                                const infoIsOpen = formHeight !== "50px";
                                handleToggleForm(e, !infoIsOpen);
                              }}
                            ></div>
                          </Grid>
                        </AccordionSummary>

                        <AccordionDetails classes={{ root: `tab_${tabValue}` }}>
                          {/*  BID PACKAGE DETAILS FIELDS */}
                          <Grid
                            container
                            className="tab_content details"
                            style={{ display: tabValue === 0 ? "flex" : "none", overflow: "auto" }}
                          >
                            <Grid container>
                              <Typography className="required_fields_label">required fields</Typography>
                              <Grid container direction="row" className="required_fields">
                                <Grid container>
                                  <FormikTextField className="field textfield title" label="Title" name="title" />
                                  <FormikSelectField
                                    className="field selectfield positions"
                                    name="positions"
                                    label="Assignment Positions"
                                    options={positions}
                                    format={(titles: string[]) => getFullPositions(titles)}
                                    parse={(options: IPosition) => (options ? [options.title] : [])}
                                    getOptionLabel={(pos: IPosition) => `${pos?.title} (${pos?.description})`}
                                    getOptionValue={(pos: IPosition) => pos?._id}
                                    isClearable={true}
                                    hideSelectedOptions={false}
                                    menuPortalTarget={mosaicPane}
                                  />
                                  <FormikCheckboxField
                                    className="field checkboxfield isdisabled"
                                    label="Is Disabled"
                                    name="isDisabled"
                                  />
                                </Grid>
                                <hr className="grid_item_divider" />

                                <Grid
                                  container
                                  direction="column"
                                  className="bid_package_datetimes"
                                  alignItems="flex-start"
                                >
                                  <Grid container style={{ width: 600 }} justifyContent="space-between">
                                    <div className="bid_window_color_swatch" />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Window Open"
                                      name="bidWindowOpen"
                                    />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Window Close"
                                      name="bidWindowClose"
                                    />
                                  </Grid>
                                  <Grid container style={{ width: 600 }} justifyContent="space-between">
                                    <div className="bid_block_color_swatch" />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Block Start"
                                      name="bidBlockStart"
                                    />
                                    <FormikDateTimeField
                                      className="field datetimefield"
                                      label="Bid Block End"
                                      name="bidBlockEnd"
                                    />
                                  </Grid>
                                  {/* <Grid container style={{ width: 600 }}>
                                  <div className="release_color_swatch" />
                                  <FormikDateTimeField
                                    className="field datetimefield"
                                    label="Release To Crew"
                                    name="releaseScheduleAt"
                                  />
                                </Grid> */}
                                </Grid>
                                <hr className="grid_item_divider" />
                                <NFATypeCountField
                                  value={values?.nonFlyingAssignments}
                                  onChange={(newNFACounts) => setFieldValue("nonFlyingAssignments", newNFACounts)}
                                  mosaicPane={mosaicPane}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          {/*  BID PACKAGE ADD CREW */}
                          <Grid
                            container
                            direction="column"
                            className="tab_content add_crew"
                            style={{ display: tabValue === 1 ? "flex" : "none", width: "100%", height: "100%" }}
                          >
                            {tabValue === 1 && ( // Added for now to resolve a bad setState in CrewGroupsField
                              <CrewGroupsField
                                id={id}
                                path={path}
                                isNew={!values?._id}
                                crewBids={crewBids}
                                initialCrewGroups={initialFormValues?.crewGroups}
                                crewGroups={values?.crewGroups}
                                populateCrew={populateCrew}
                                setPopulateCrew={setPopulateCrew}
                                mosaicPane={mosaicPane}
                              />
                            )}
                          </Grid>
                          {/*  BID PACKAGE ADD PAIRINGS */}
                          <Grid
                            container
                            direction="column"
                            className="tab_content add_pairings"
                            style={{ display: tabValue === 2 ? "flex" : "none", width: "100%", height: "100%" }}
                          >
                            {values?._id && (
                              <PairingSearch
                                packageId={values?._id}
                                id={id}
                                path={path}
                                setBidPairingCount={setBidPairingCount}
                                bidPairingprogressPercentage={bidPairingprogressPercentage}
                              />
                            )}
                          </Grid>
                          {/*  BID PACKAGE VIEW BID PAIRINGS */}
                          <Grid
                            container
                            direction="column"
                            className="tab_content view_bid_pairings"
                            style={{ display: tabValue === 3 ? "flex" : "none", width: "100%", height: "100%" }}
                          >
                            {values?._id && (
                              <BidPairingView
                                packageId={values?._id}
                                id={id}
                                path={path}
                                setBidPairingCount={setBidPairingCount}
                                bidPairingProgressStatus={bidPairingProgressStatus}
                                bidPairingprogressPercentage={bidPairingprogressPercentage}
                              />
                            )}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </TabStyles>
      {activeModal && (
        <DialogContent sx={(theme) => BidPackageEditorStyles(theme).ModalDialogStyle}>
          <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
            <ModalTitle title={activeModal.title} />
            <ModalMessage message={activeModal.message} />
            {/* errorText && <ModalErrorText error={errorText}></ModalErrorText> */}
            <DialogActions>
              <ModalButton label="Cancel" handler={activeModal.cancel}></ModalButton>
              <ModalButton label={activeModal.confirmLabel} handler={activeModal.confirm}></ModalButton>
            </DialogActions>
          </div>
        </DialogContent>
      )}
    </>
  );
};
